import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AiApiToken, AiApiTokenCreated } from '../models/models';
import api from './api';

// ── State ────────────────────────────────────────────────────────────────────

interface AiApiTokenState {
  aiTokenData: AiApiToken[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedAiToken: AiApiToken | null;
  /** Secret en clair du dernier token généré — affiché UNE SEULE FOIS puis effacé. */
  newPlainToken: string | null;
}

const initialState: AiApiTokenState = {
  aiTokenData: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
  loading: false,
  error: null,
  successMessage: null,
  selectedAiToken: null,
  newPlainToken: null,
};

// ── Thunks ───────────────────────────────────────────────────────────────────

export const fetchAiTokenData = createAsyncThunk<
  { data: AiApiToken[]; total: number; totalPages: number; currentPage: number },
  { limit: number; offset: number },
  { rejectValue: string }
>('aiToken/fetchAll', async (params, thunkAPI) => {
  try {
    const response = await api.get('/aiToken', { params });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur chargement des tokens IA');
  }
});

export const generateAiToken = createAsyncThunk<
  AiApiTokenCreated,
  { nom: string; user_id?: number },
  { rejectValue: string }
>('aiToken/generate', async (data, thunkAPI) => {
  try {
    const response = await api.post('/aiToken', data);
    return response.data as AiApiTokenCreated;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur génération du token IA');
  }
});

export const revokeAiToken = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('aiToken/revoke', async (id, thunkAPI) => {
  try {
    await api.delete('/aiToken', { params: { id } });
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || 'Erreur révocation du token IA');
  }
});

// ── Slice ────────────────────────────────────────────────────────────────────

const aiApiTokenSlice = createSlice({
  name: 'aiToken',
  initialState,
  reducers: {
    setSelectedAiToken: (state, action: PayloadAction<AiApiToken | null>) => {
      state.selectedAiToken = action.payload;
    },
    clearAiTokenData: (state) => {
      state.aiTokenData = [];
      state.total = 0;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    /** À appeler juste après que l'utilisateur a copié le secret affiché. */
    clearNewPlainToken: (state) => {
      state.newPlainToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAiTokenData
      .addCase(fetchAiTokenData.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAiTokenData.fulfilled, (state, action) => {
        state.loading = false;
        state.aiTokenData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchAiTokenData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // generateAiToken
      .addCase(generateAiToken.pending, (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(generateAiToken.fulfilled, (state, action) => {
        state.loading = false;
        state.newPlainToken = action.payload.plainToken;
        state.successMessage = 'Token IA généré !';
      })
      .addCase(generateAiToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // revokeAiToken
      .addCase(revokeAiToken.pending, (state) => { state.loading = true; })
      .addCase(revokeAiToken.fulfilled, (state, action) => {
        state.loading = false;
        state.aiTokenData = state.aiTokenData.filter((t) => t.id !== action.payload);
        state.successMessage = 'Token IA révoqué.';
      })
      .addCase(revokeAiToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedAiToken, clearAiTokenData, clearMessages, clearNewPlainToken } = aiApiTokenSlice.actions;
export default aiApiTokenSlice.reducer;
